import { useState } from 'react';

import { TrackAssessmentCategory } from '@backrs_ui/common-ui/models/trackAssessment';
import { appStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import { AppIcon } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';
import { useContainerQuery } from '@backrs_ui/common-ui/src/hooks/useContainerQuery';
import { TRACK_CODE_TO_NAME } from '@backrs_ui/common-ui/src/models/careerPath';
import { TrackCode } from '@backrs_ui/common-ui/src/models/careerTrack';

import {
  Box,
  Button,
  SxProps,
  Theme,
  Typography,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import ReactMarkdown from 'react-markdown';

import {
  UnlockItem,
  UnlockItemProps,
  NAME_COLUMN_WIDTH,
  COLUMN_WIDTH,
} from './AreYouOnTrackSummaryItem';
import { HorizontalCareerTrackBar } from './HorizontalCareerTrackBar';

const ARE_YOU_ON_TRACK_SUMMARY_STRINGS = appStrings.modules.assessMyTrack.areYouOnTrackSummary;

const SLUG_TO_SHORTNAME: Record<TrackAssessmentCategory['slug'], string> = {
  'academic-foundations': 'Academic',
  'real-world-experience': 'Achievement',
  'mindsets-and-skills': 'Skills',
  'community-of-success': 'Communitities',
  none: 'None',
};

type AreYouOnTrackSummaryProps = Readonly<{
  careerTrack?: TrackCode;
  profileImageUrl?: string;
  buttonOnClick: () => void;
  compact?: boolean;

  academicFoundations: UnlockItemProps;
  realWorldExperience: UnlockItemProps;
  mindsetsAndSkills: UnlockItemProps;
  communityOfSuccess: UnlockItemProps;
}>;

export const AreYouOnTrackSummary = ({
  careerTrack,
  profileImageUrl,
  buttonOnClick,
  compact,
  academicFoundations,
  realWorldExperience,
  mindsetsAndSkills,
  communityOfSuccess,
}: AreYouOnTrackSummaryProps) => {
  const [containerRef, isContainerCompact] = useContainerQuery(900);
  const isMobile = compact ?? isContainerCompact;
  const [selectedCategoryIndex, setSelectedCategoryIndex] = useState(0);
  const careerTrackName = careerTrack ? TRACK_CODE_TO_NAME[careerTrack] : '';

  const isEmptyState = !careerTrack;

  const categories = isEmptyState
    ? [academicFoundations, realWorldExperience, mindsetsAndSkills, communityOfSuccess]
    : [academicFoundations, realWorldExperience, mindsetsAndSkills, communityOfSuccess].filter(
        (category) => category.strengths.length > 0,
      );

  const handleCategorySelect = (event: SelectChangeEvent<number>) => {
    setSelectedCategoryIndex(event.target.value as number);
  };

  const renderCTAButton = (isEmptyState: boolean) => {
    if (isEmptyState) {
      return (
        <Button
          id="are-you-on-track-assess-btn"
          sx={buttonSx}
          hierarchy="Primary"
          size="md"
          onClick={buttonOnClick}
        >
          <AppIcon iconName="Speedometer02" diameter="20px" color="Colors/Base/White" />
          {ARE_YOU_ON_TRACK_SUMMARY_STRINGS.assessYourTrack}
        </Button>
      );
    }

    return (
      <Button
        id="are-you-on-track-check-progress-btn"
        sx={buttonSx}
        hierarchy="Secondary gray"
        size="md"
        onClick={buttonOnClick}
      >
        {isMobile
          ? ARE_YOU_ON_TRACK_SUMMARY_STRINGS.learnMoreMobile
          : ARE_YOU_ON_TRACK_SUMMARY_STRINGS.learnMore}
        <AppIcon
          iconName="ArrowCircleUpRight"
          diameter="20px"
          color="Colors/Foreground/fg-secondary"
        />
      </Button>
    );
  };

  return (
    <Box ref={containerRef} sx={containerSx}>
      <Box sx={titleAndDescriptionContainerSx(isMobile)}>
        <Box sx={titleAndDescriptionSx}>
          <Typography variant="display-sm-semibold">
            {ARE_YOU_ON_TRACK_SUMMARY_STRINGS.title}
          </Typography>
          <Typography
            variant="text-sm-regular"
            color={({ backrs }) => backrs.colors['Colors/Text/text-tertiary']}
          >
            <ReactMarkdown>
              {isEmptyState
                ? ARE_YOU_ON_TRACK_SUMMARY_STRINGS.descriptionEmptyState
                : ARE_YOU_ON_TRACK_SUMMARY_STRINGS.description(careerTrackName)}
            </ReactMarkdown>
          </Typography>
        </Box>
        {!isMobile && renderCTAButton(isEmptyState)}
      </Box>
      {careerTrack ? (
        <Box sx={barContainerSx}>
          <HorizontalCareerTrackBar currentTrack={careerTrack} profileImageUrl={profileImageUrl} />
        </Box>
      ) : (
        <Box sx={emptyStateBarSx} />
      )}

      {isMobile && categories.length > 1 && (
        <Box sx={mobileCTAContainerSx}>
          {renderCTAButton(isEmptyState)}
          <Box sx={mobileDropdownContainerSx}>
            <Select
              id="are-you-on-track-category-select"
              value={selectedCategoryIndex}
              onChange={handleCategorySelect}
              sx={mobileDropdownSx}
              displayEmpty
            >
              {categories.map((category, index) => (
                <MenuItem key={category.category.slug} value={index}>
                  <Box sx={menuItemContentSx}>
                    <Typography>{SLUG_TO_SHORTNAME[category.category.slug]}</Typography>
                    <AppIcon
                      iconName="ChevronDown"
                      diameter="20px"
                      color="Colors/Foreground/fg-secondary"
                    />
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>
      )}

      {!isMobile && (
        <Box sx={tableHeaderSx(isEmptyState)}>
          <Box sx={headerImageColumnSx}>
            <Typography variant="text-xs-semibold" sx={headerTextSx}>
              {ARE_YOU_ON_TRACK_SUMMARY_STRINGS.theFourUnlocks}
            </Typography>
          </Box>
          <Box sx={headerCategoryColumnSx}>
            <Typography variant="text-xs-semibold" sx={headerTextSx}>
              {ARE_YOU_ON_TRACK_SUMMARY_STRINGS.fullName}
            </Typography>
          </Box>
          <Box sx={headerSectionColumnSx}>
            <Typography variant="text-xs-semibold" sx={headerTextSx}>
              {ARE_YOU_ON_TRACK_SUMMARY_STRINGS.yourStrengths}
            </Typography>
          </Box>
          <Box sx={headerSectionColumnSx}>
            <Typography variant="text-xs-semibold" sx={headerTextSx}>
              {ARE_YOU_ON_TRACK_SUMMARY_STRINGS.opportunitiesToGrow}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Unlock Items */}
      {isMobile ? (
        categories[selectedCategoryIndex] && (
          <UnlockItem
            {...categories[selectedCategoryIndex]}
            isMobile={true}
            isEmptyState={isEmptyState}
          />
        )
      ) : (
        <>
          {(isEmptyState || academicFoundations.strengths.length > 0) && (
            <UnlockItem {...academicFoundations} isMobile={false} isEmptyState={isEmptyState} />
          )}
          {(isEmptyState || realWorldExperience.strengths.length > 0) && (
            <UnlockItem {...realWorldExperience} isMobile={false} isEmptyState={isEmptyState} />
          )}
          {(isEmptyState || mindsetsAndSkills.strengths.length > 0) && (
            <UnlockItem {...mindsetsAndSkills} isMobile={false} isEmptyState={isEmptyState} />
          )}
          {(isEmptyState || communityOfSuccess.strengths.length > 0) && (
            <UnlockItem {...communityOfSuccess} isMobile={false} isEmptyState={isEmptyState} />
          )}
        </>
      )}
    </Box>
  );
};

const titleAndDescriptionContainerSx =
  (isMobile: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    flexDirection: isMobile ? 'column-reverse' : 'row',
    justifyContent: 'space-between',
    alignItems: isMobile ? 'flex-start' : 'center',
    padding: backrs.spacing['spacing-2xl'],
    gap: isMobile ? 0 : backrs.spacing['spacing-2xl'],
  });

const titleAndDescriptionSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
};

const buttonSx: SxProps<Theme> = {
  whiteSpace: 'nowrap',
  flexShrink: 0,
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: backrs.radius['radius-xl'],
  border: `1px solid ${backrs.colors['Colors/Foreground/fg-secondary']}`,
  backgroundColor: backrs.colors['Colors/Base/White'],
  overflow: 'hidden',
});

const tableHeaderSx =
  (isEmptyState: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    gap: backrs.spacing['spacing-2xl'],
    px: backrs.spacing['spacing-2xl'],
    py: backrs.spacing['spacing-lg'],
    borderTop: isEmptyState
      ? `1px solid ${backrs.colors['Colors/Foreground/fg-quinary']}`
      : `1px solid ${backrs.colors['Colors/Foreground/fg-secondary']}`,
  });

const headerImageColumnSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  minWidth: COLUMN_WIDTH,
  maxWidth: COLUMN_WIDTH,
};

const headerCategoryColumnSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  minWidth: NAME_COLUMN_WIDTH,
  maxWidth: NAME_COLUMN_WIDTH,
};

const headerSectionColumnSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flex: 1,
};

const headerTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-quaternary'],
});

const barContainerSx: SxProps<Theme> = ({ backrs }) => ({
  width: '95%',
  marginBottom: backrs.spacing['spacing-4xl'],
});

const mobileDropdownContainerSx: SxProps<Theme> = {
  width: '100%',
};

const mobileDropdownSx: SxProps<Theme> = ({ backrs }) => ({
  width: '100%',
  '& .MuiSelect-select': {
    padding: `${backrs.spacing['spacing-xl']} ${backrs.spacing['spacing-lg']}`,
  },
  '& .MuiMenuItem-root': {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },
});

const emptyStateBarSx: SxProps<Theme> = ({ backrs }) => ({
  width: '95%',
  marginBottom: backrs.spacing['spacing-2xl'],
  backgroundColor: backrs.colors['Colors/Background/bg-quaternary'],
  height: '8px',
  borderRadius: backrs.radius['radius-full'],
});

const mobileCTAContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
  gap: backrs.spacing['spacing-md'],
  padding: backrs.spacing['spacing-md'],
});

const menuItemContentSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
  paddingRight: '8px',
};
